import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Target, CalendarCheck2, Code2, ClipboardList,
  TrendingUp, Award, FileSpreadsheet, Bell, Settings, Sun, Moon, Activity, LogOut,
  BarChart3, Clapperboard, KeyRound, X,
} from "lucide-react";
import { useApp, MONTHS } from "../lib/store";
import { api } from "../lib/apiClient";
import { toast } from "sonner";

const A = ["super_admin", "manager", "hr"];
const ALL = ["super_admin", "manager", "hr", "employee"];
const NAV = [
  { to: "/", label: "Main Dashboard", icon: LayoutDashboard, id: "dashboard", roles: A },
  { to: "/employees", label: "Employee Directory", icon: Users, id: "employees", roles: A },
  { to: "/kra", label: "KRA Framework", icon: Target, id: "kra-config", roles: ALL },
  { to: "/content-calendar", label: "Content Calendar", icon: CalendarCheck2, id: "content-calendar", roles: ALL },
  { to: "/dev-tasks", label: "Dev Tasks (48h)", icon: Code2, id: "dev-tracker", roles: ["super_admin", "employee"], emp: ["yograj", "dinesh"] },
  { to: "/monthly", label: "Monthly Reviews", icon: ClipboardList, id: "monthly-reviews", roles: ALL },
  { to: "/quarterly", label: "Quarterly Reviews", icon: TrendingUp, id: "quarterly-reviews", roles: ALL },
  { to: "/yearly", label: "Yearly & Increment", icon: Award, id: "yearly-increment", roles: ALL },
  { to: "/growth", label: "SMM Growth Tracker", icon: BarChart3, id: "smm-growth", roles: A },
  { to: "/shoots", label: "Shoot & Creator Board", icon: Clapperboard, id: "smm-shoots", roles: A },
  { to: "/reports", label: "Reports & Exports", icon: FileSpreadsheet, id: "reports-export", roles: A },
  { to: "/notifications", label: "Notifications", icon: Bell, id: "notifications", roles: ALL },
  { to: "/settings", label: "Settings & Audit", icon: Settings, id: "settings", roles: ["super_admin"] },
];

export default function Layout({ children }) {
  const { role, roleLabel, user, logout, theme, setTheme, month, setMonth, year, setYear } = useApp();
  const [unread, setUnread] = useState(0);
  const [pwModal, setPwModal] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    api.notifications().then((n) => {
      const read = JSON.parse(localStorage.getItem("kra_read") || "[]");
      setUnread(n.filter((x) => !read.includes(x.id)).length);
    }).catch(() => {});
  }, [loc.pathname]);

  const visibleNav = NAV.filter((n) => n.roles.includes(role) && (role !== "employee" || !n.emp || n.emp.includes(user?.employee_id)));
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 fixed inset-y-0 left-0 z-40 surface border-r border-subtle hidden lg:flex flex-col">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-subtle">
          <div className="w-8 h-8 rounded-lg brand-bg flex items-center justify-center"><Activity size={18} className="text-white" /></div>
          <div className="leading-tight">
            <p className="font-display font-bold text-sm">Media Team KRA</p>
            <p className="text-[10px] text-muted-c tracking-wide">PERFORMANCE · FY 2026-27</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {visibleNav.map((n) => (
            <NavLink key={n.id} to={n.to} end={n.to === "/"} data-testid={`nav-item-${n.id}`}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
              <n.icon size={17} strokeWidth={2} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-subtle">
          <div className="elevated rounded-xl p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center mono text-xs font-bold shrink-0" style={{ background: "var(--brand-glow)", color: "var(--brand)" }}>{initials}</div>
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold text-sm truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-c uppercase tracking-wider">{roleLabel}</p>
            </div>
            <button onClick={() => setPwModal(true)} className="text-muted-c hover:brand-text transition-colors" data-testid="account-password-button" title="Change password"><KeyRound size={16} /></button>
            <button onClick={logout} className="text-muted-c hover:text-red-500 transition-colors" data-testid="logout-button" title="Sign out"><LogOut size={17} /></button>
          </div>
        </div>
      </aside>
      {pwModal && <PasswordModal onClose={() => setPwModal(false)} />}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 sticky top-0 z-30 border-b border-subtle px-4 sm:px-6 flex items-center justify-between gap-3"
          style={{ background: "color-mix(in srgb, var(--bg) 92%, transparent)", backdropFilter: "blur(12px)" }}>
          <div className="flex items-center gap-2">
            <Activity size={18} className="brand-text lg:hidden" />
            <span className="text-xs sm:text-sm font-semibold text-secondary">Media Team KRA & Performance Management</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1.5 elevated rounded-lg px-2 py-1 border border-subtle">
              <select data-testid="global-month-select" value={month} onChange={(e) => setMonth(+e.target.value)}
                className="bg-transparent text-sm font-medium outline-none cursor-pointer" style={{ color: "var(--text-primary)" }}>
                {MONTHS.map((m) => <option key={m.v} value={m.v} className="text-black">{m.n}</option>)}
              </select>
              <select data-testid="global-year-select" value={year} onChange={(e) => setYear(+e.target.value)}
                className="bg-transparent text-sm font-medium outline-none cursor-pointer" style={{ color: "var(--text-primary)" }}>
                {[2025, 2026, 2027].map((y) => <option key={y} value={y} className="text-black">{y}</option>)}
              </select>
            </div>
            <span className="hidden sm:inline-flex pill px-3 py-1.5 text-xs font-semibold brand-text border-subtle" data-testid="current-role-badge">{roleLabel}</span>
            <NavLink to="/notifications" className="relative w-9 h-9 rounded-lg elevated border border-subtle flex items-center justify-center hover:brand-text transition-colors" data-testid="header-notification-btn">
              <Bell size={17} />
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
            </NavLink>
            <button data-testid="theme-toggle-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="w-9 h-9 rounded-lg elevated border border-subtle flex items-center justify-center hover:brand-text transition-colors">
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 max-w-[1600px]">{children}</main>
      </div>
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const save = async () => {
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    try { await api.changePassword(cur, nw); toast.success("Password changed"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle"><h3 className="font-display font-semibold">Change Password</h3><button onClick={onClose}><X size={18} className="text-muted-c" /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Current Password</label><input type="password" className="input-c" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" /></div>
          <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">New Password</label><input type="password" className="input-c" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle"><button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button><button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-password-button">Update</button></div>
      </div>
    </div>
  );
}
